import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';

// Log times are stored in UTC; the venues (and the people reading this) are all
// Pacific, so every date/time shown or filtered here is converted to Pacific.
const LOGS_TZ = 'America/Los_Angeles';

function tzOffsetMs(date, timeZone) {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return tzDate.getTime() - utcDate.getTime();
}

// 'YYYY-MM-DD' Pacific calendar date -> UTC Date for midnight of that day in Pacific
function pacificMidnightUTC(dateStr) {
  const approx = new Date(`${dateStr}T00:00:00.000Z`);
  return new Date(approx.getTime() - tzOffsetMs(approx, LOGS_TZ));
}

function pacificDateStr(date) {
  return date.toLocaleDateString('en-CA', { timeZone: LOGS_TZ }); // YYYY-MM-DD
}

function fmtPacific(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const datePart = d.toLocaleDateString('en-CA', { timeZone: LOGS_TZ });
  const timePart = d.toLocaleTimeString('en-GB', { timeZone: LOGS_TZ });
  return `${datePart}  ${timePart}`;
}

export default function PortalLogs() {
  const [options, setOptions] = useState({ clientids: [], users: [] });
  const [range, setRange] = useState('today'); // 'today' | 'yesterday' | '7' | 'custom'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [clientid, setClientid] = useState('');
  const [user, setUser] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null); // { loading, message }

  useEffect(() => {
    api.getPortalLogOptions().then(opts => {
      setOptions(opts);
      if (opts.clientids.length === 1) setClientid(opts.clientids[0]);
      if (opts.users.length === 1) setUser(opts.users[0]);
    }).catch(console.error);
  }, []);

  const { start, end } = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const todayStart = pacificMidnightUTC(pacificDateStr(new Date()));
    if (range === 'today') {
      return { start: todayStart.toISOString(), end: new Date(todayStart.getTime() + dayMs).toISOString() };
    }
    if (range === 'yesterday') {
      return { start: new Date(todayStart.getTime() - dayMs).toISOString(), end: todayStart.toISOString() };
    }
    if (range === '7') {
      return { start: new Date(todayStart.getTime() - 6 * dayMs).toISOString(), end: new Date(todayStart.getTime() + dayMs).toISOString() };
    }
    if (customStart && customEnd) {
      return { start: pacificMidnightUTC(customStart).toISOString(), end: new Date(pacificMidnightUTC(customEnd).getTime() + dayMs).toISOString() };
    }
    return { start: undefined, end: undefined };
  }, [range, customStart, customEnd]);

  useEffect(() => {
    if (range === 'custom' && (!customStart || !customEnd)) { setLogs([]); return; }
    setLoading(true);
    api.getPortalLogs({ clientid, user, start, end })
      .then(setLogs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [range, customStart, customEnd, clientid, user, start, end]);

  async function openLog(id) {
    setViewing({ loading: true, message: '' });
    try {
      const full = await api.getPortalLog(id);
      setViewing({ loading: false, message: full.message || '(no message)' });
    } catch (err) {
      setViewing({ loading: false, message: `Error: ${err.message}` });
    }
  }

  const RANGE_LABELS = { today: 'Today', yesterday: 'Yesterday', '7': 'Last 7 Days', custom: 'Date Range' };

  return (
    <div>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['today', 'yesterday', '7', 'custom'].map(r => (
            <button
              key={r}
              className={`toggle-btn${range === r ? ' active' : ''}`}
              onClick={() => setRange(r)}
            >{RANGE_LABELS[r]}</button>
          ))}
        </div>

        {range === 'custom' && (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>From</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={{ width: 150 }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>To</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={{ width: 150 }} />
            </div>
          </>
        )}

        {options.clientids.length > 1 && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Property</label>
            <select value={clientid} onChange={e => setClientid(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">All Properties</option>
              {options.clientids.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {options.users.length > 1 && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>User ID</label>
            <select value={user} onChange={e => setUser(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">All Users</option>
              {options.users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        )}

        <span style={{ color: '#718096', fontSize: 13, marginLeft: 'auto' }}>
          {loading ? 'Loading...' : `${logs.length} log${logs.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {!loading && logs.length === 0 && (
        <div className="card" style={{ color: '#718096' }}>
          {range === 'custom' && (!customStart || !customEnd) ? 'Select a start and end date.' : 'No logs found for this period.'}
        </div>
      )}

      {logs.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th></th>
                <th style={{ whiteSpace: 'nowrap' }}>Time (PT)</th>
                <th>Property</th>
                <th>User ID</th>
                <th>Search Time</th>
                <th>Booking Window</th>
                <th>Criteria</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={String(l._id)} style={{ background: i % 2 === 0 ? '#ffffff' : '#f7f8fa' }}>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => openLog(l._id)}>View</button></td>
                  <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>{fmtPacific(l.updatedAt)}</td>
                  <td>{l.clientid}</td>
                  <td>{l.user}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.searchtime || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>{l.start && l.end ? `${l.start} – ${l.end}` : '—'}</td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{(l.shiftCriteria || []).length} target{(l.shiftCriteria || []).length !== 1 ? 's' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setViewing(null)}
        >
          <div
            style={{ background: '#1a1a2e', color: '#e2e8f0', borderRadius: 8, maxWidth: 900, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #2d3748' }}>
              <strong style={{ fontSize: 14 }}>Log Detail</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewing(null)}>Close</button>
            </div>
            <div className="log-detail-body" style={{ padding: 16, overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6 }}>
              {viewing.loading
                ? 'Loading...'
                : <div dangerouslySetInnerHTML={{ __html: viewing.message }} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
